
import { Component, Model, Prop, Vue, Watch } from "vue-property-decorator";
import * as Models from "@gigalot/data-models";
import { formatDoseLabel as formatDoseLabelHelper } from "../helpers/doses";

@Component
export default class TreatmentSelectInput extends Vue {
  @Model("update:selectedTreatment", { type: Object }) readonly selectedTreatment!: Models.Treatment | null;
  @Prop({ type: Array, default: () => [] }) readonly treatments!: Models.Treatment[];
  @Prop({ type: Array, default: () => [] }) readonly appliedDoses!: Models.AppliedDose[];
  @Prop({ type: Boolean, default: false }) readonly showDoseDetails!: boolean;
  @Prop({ type: Boolean, default: false }) readonly alwaysRequireMass!: boolean;
  @Prop({ type: Boolean, default: true }) readonly massValid!: boolean;
  @Prop({ type: Boolean, default: false }) readonly useAverageMass!: boolean;
  @Prop({ type: String, default: "Treatment" }) readonly label!: string;
  @Prop({ type: Boolean, default: true }) readonly isRequired!: boolean;
  @Prop({ type: String, default: "Select Treatment" }) readonly dialogTitle!: string;
  @Prop({ type: String, default: "None selected" }) readonly emptyText!: string;
  @Prop({ type: [String, Number], default: 640 }) readonly maxWidth!: string | number;
  @Prop({ type: Boolean, default: false }) readonly isLocked!: boolean;

  dialog: boolean = false;
  search: string = "";
  detailsDialog: boolean = false;
  details: Models.Treatment | null = null;
  draftTreatment: Models.Treatment | null = null;
  draftTreatmentKey: string = "";

  get filteredTreatments(): Models.Treatment[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.treatments;
    return this.treatments.filter((t) => (t.description || "").toLowerCase().includes(term));
  }

  doseKey(dose: Models.Dose, idx: number): string | number {
    return (dose.item && dose.item.description) || idx;
  }

  doseDescription(dose: Models.Dose, idx: number): string {
    return (dose.item && dose.item.description) || `Dose ${idx + 1}`;
  }

  openTreatmentDetails(treatment: Models.Treatment) {
    this.details = treatment;
    this.detailsDialog = true;
  }

  closeTreatmentDetails() {
    this.detailsDialog = false;
    this.details = null;
  }

  openDialog() {
    if (this.isLocked) {
      return;
    }

    this.search = "";
    this.draftTreatment = this.selectedTreatment;
    this.draftTreatmentKey = this.treatmentKey(this.draftTreatment);
    this.dialog = true;
  }

  closeDialog() {
    this.dialog = false;
    this.draftTreatment = this.selectedTreatment;
    this.draftTreatmentKey = this.treatmentKey(this.draftTreatment);
  }

  @Watch("dialog")
  onDialogChanged(nextValue: boolean, previousValue: boolean) {
    if (previousValue && !nextValue) {
      this.$nextTick(() => requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur()));
    }
  }

  treatmentKey(treatment: Models.Treatment | null): string {
    return (treatment?.guid || treatment?.description || "").toString();
  }

  isSelectedTreatment(treatment: Models.Treatment) {
    return this.treatmentKey(this.draftTreatment) === this.treatmentKey(treatment);
  }

  selectTreatment(treatment: Models.Treatment) {
    this.draftTreatment = treatment;
    this.draftTreatmentKey = this.treatmentKey(treatment);
  }

  @Watch("draftTreatmentKey")
  onDraftTreatmentKeyChanged(nextValue: string) {
    if (!nextValue) {
      this.draftTreatment = null;
      return;
    }
    const match = this.treatments.find((t) => this.treatmentKey(t) === nextValue);
    if (match) this.draftTreatment = match;
  }

  clearTreatmentSelection() {
    this.$emit("update:selectedTreatment", null);
  }

  saveSelection() {
    this.$emit("update:selectedTreatment", this.draftTreatment ?? null);
    this.closeDialog();
  }

  get selectedTreatmentDoses(): Models.Dose[] {
    return this.selectedTreatment?.doses ?? [];
  }

  get shouldShowMassRequired(): boolean {
    return (this.alwaysRequireMass || this.useAverageMass) && !this.massValid;
  }

  get doseTypes(): { [doseDescription: string]: "cc-mass-dependent" | "constant" } {
    const ret: { [doseDescription: string]: "cc-mass-dependent" | "constant" } = {};
    for (const dose of this.selectedTreatmentDoses) {
      if (dose.item && dose.type) ret[dose.item.description] = dose.type;
    }
    return ret;
  }

  get appliedDosesMassDependent() {
    return this.appliedDoses.filter((ad) => this.doseTypes[ad.item.description] === "cc-mass-dependent");
  }

  get appliedDosesMassConstant() {
    return this.appliedDoses.filter((ad) => this.doseTypes[ad.item.description] === "constant");
  }

  formatDoseLabel(dose: Models.Dose): string {
    return formatDoseLabelHelper(dose);
  }

  formatAppliedDoseDetail(appliedDose: Models.AppliedDose): string {
    const matchingDose = this.selectedTreatmentDoses.find((d) => d.item?.description === appliedDose.item.description);
    if (!matchingDose) return "";
    return this.formatDoseLabel(matchingDose);
  }

  doseRowClass(overallIndex: number): string {
    return overallIndex % 2 === 1 ? "dose-row-alt" : "";
  }
}
